/* Student-series host, mounted by the game shell (GamePlay).
 *
 * Auto-start rule (design doc §1.2): the first time a game opens on round 1
 * with no resolved quarters — the student's real Q1 is the sandbox; their
 * tutorial decisions ARE their Q1. Never auto-starts again once dismissed or
 * completed; "? Tour" in the shell header restarts explicitly. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo } from 'react';
import { Engine, SEGMENTS, fmt$ } from '@venturewrist/engine';
import { useAuth } from '../hooks/queries';
import { useDecisions } from '../store/decisions';
import { buildWorkingGame } from '../lib/designState';
import { api } from '../lib/api';
import type { GameDetail } from '../lib/types';
import { useTutorial } from './useTutorial';
import { TutorialHost } from './TutorialHost';
import { studentSeries } from './steps.student';
import { shapeOf, type TutCtx } from './types';

const Eng = Engine as any;

/** Progress key for a team's student tour. Team-scoped (not just per-user) so a
 *  student who joins or creates a new team plays the tour again on that team's
 *  first quarter — one namespace in User.tutorialState per team. */
export const studentScopeKey = (teamId: string): string => 'student:' + teamId;

/** Reference price of the first active model's target segment ({refPrice}). */
function refPriceOf(wg: any): string {
  try {
    const m = (Eng.activeModels(wg) as any[])[0];
    const seg = (SEGMENTS as any[]).find((s) => s.id === m?.targetSeg);
    if (seg) return fmt$(seg.refPrice);
  } catch { /* fall through */ }
  return '$249';
}

export function StudentTutorial({ detail, tab, resultOpen }: {
  detail: GameDetail;
  tab: string;
  resultOpen: boolean;
}) {
  const { user } = useAuth();
  const draft = useDecisions((z) => z.draft);
  const hydrated = useTutorial((z) => z.hydrated);
  const active = useTutorial((z) => z.active);
  const pkey = useTutorial((z) => z.pkey);
  const signals = useTutorial((z) => z.signals);

  const s = detail.state as any;
  const teamId = detail.game.teamId;
  const scopeKey = studentScopeKey(teamId);
  // Auto-start is a *student* rule (§1.2) — an instructor peeking into a
  // student's fresh game shouldn't trigger it (the ? Tour button still works).
  const eligible =
    user?.role === 'student' &&
    s.round === 1 && detail.snapshots.length === 0 && detail.game.status !== 'finished';

  // Hydrate once per session from the server-persisted progress.
  useEffect(() => {
    if (user) useTutorial.getState().hydrate(user.tutorialState, user.id);
  }, [user]);

  // Switching games mid-session (a student on more than one team): a tour left
  // running/finished for another team must not leak onto this game, nor block
  // this team's own auto-start. Tear it down before the auto-start effect runs.
  useEffect(() => {
    const st = useTutorial.getState();
    if (st.active === 'student' && st.pkey !== scopeKey) st.stop();
  }, [scopeKey]);

  // Auto-start on the first eligible visit (start() no-ops if dismissed/completed
  // for THIS team's key, so a completed tour never re-triggers — but a new team's
  // fresh key does).
  useEffect(() => {
    if (hydrated && eligible && useTutorial.getState().active == null) {
      useTutorial.getState().start('student', shapeOf(studentSeries), scopeKey);
    }
  }, [hydrated, eligible, scopeKey]);

  // Step instrumentation → ActivityLog type:'tutorial' under the game's team.
  useEffect(() => {
    useTutorial.getState().setLogSink((e) => {
      api.logActivity({ type: 'tutorial', teamId, payload: { ...e } }).catch(() => { /* fire-and-forget */ });
    });
    return () => useTutorial.getState().setLogSink(null);
  }, [teamId]);

  // Guard by the scoped key too, so on a game switch the previous team's overlay
  // doesn't flash before the effects above reconcile.
  const running = active === 'student' && pkey === scopeKey;
  const wg = useMemo(
    () => (running ? buildWorkingGame(detail.state, draft) : null),
    [running, detail.state, draft],
  );
  if (!running) return null;

  const ctx: TutCtx = {
    tab,
    draft,
    game: wg,
    maxRounds: s.maxRounds ?? detail.game.maxRounds,
    refPrice: refPriceOf(wg),
    signals,
    resultOpen,
    submitted: detail.snapshots.length > 0 || resultOpen,
    drilldownOpen: false,
  };
  return <TutorialHost series={studentSeries} ctx={ctx} />;
}
